/**
 * ProfileImageCropper — v1.0.0
 * Reusable 1:1 crop + 500×500 resize + JPEG optimization component.
 *
 * Flow: file select → crop modal (1:1 forced) → Canvas resize to 500×500 →
 *       JPEG export at quality 0.85 → returns optimized Blob (~50-150 KB).
 *
 * Props:
 *   onCropped(blob: Blob)   — called with the final optimized JPEG blob
 *   onCancel()              — called when user dismisses the crop modal
 *   trigger                 — optional custom trigger element; if omitted renders a default button
 *   currentImageUrl?        — show current image as background reference
 */

import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';

const OUTPUT_SIZE = 500;
const JPEG_QUALITY = 0.85;

interface Props {
  onCropped: (blob: Blob) => void;
  onCancel?: () => void;
  trigger?: React.ReactNode;
  currentImageUrl?: string;
  disabled?: boolean;
}

/** Canvas helper: crop + resize an image to OUTPUT_SIZE × OUTPUT_SIZE JPEG */
async function getCroppedImg(imageSrc: string, crop: Area): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      JPEG_QUALITY
    );
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });
}

export default function ProfileImageCropper({ onCropped, onCancel, trigger, disabled }: Props) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 15 * 1024 * 1024) return; // generous limit — we'll compress anyway
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = ''; // reset so same file can be reselected
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedArea) return;
    setProcessing(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedArea);
      setImageSrc(null);
      onCropped(blob);
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    onCancel?.();
  };

  return (
    <>
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleFileSelect} />

      {/* Trigger */}
      <div onClick={() => !disabled && fileInputRef.current?.click()} className={disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}>
        {trigger || (
          <button type="button" disabled={disabled} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors disabled:opacity-50">
            📷 Choose Photo
          </button>
        )}
      </div>

      {/* Crop Modal */}
      {imageSrc && (
        <div className="fixed inset-0 z-[9999] bg-black/80 overflow-y-auto overscroll-contain">
          <div className="min-h-full flex flex-col items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && handleCancel()}>
            <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-md overflow-hidden my-auto">
              <div className="px-4 py-3 border-b border-gray-700 text-center">
                <h3 className="text-white font-bold text-sm">Crop Profile Picture</h3>
                <p className="text-gray-400 text-xs mt-0.5">Drag to reposition • Scroll to zoom • 500×500 output</p>
              </div>
              <div className="relative w-full h-[260px] sm:h-[350px]">
                <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete}
                  cropShape="round" showGrid={false} style={{ containerStyle: { background: '#111' } }} />
              </div>
              <div className="px-4 py-2">
                <label className="text-[10px] text-gray-500 uppercase">Zoom</label>
                <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500" />
              </div>
              <div className="flex gap-2 p-4 border-t border-gray-700">
                <button onClick={handleCancel} className="flex-1 py-2 text-sm rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors">Cancel</button>
                <button onClick={handleConfirm} disabled={processing}
                  className="flex-1 py-2 text-sm rounded-lg bg-yellow-600 hover:bg-yellow-500 text-black font-bold transition-colors disabled:opacity-50">
                  {processing ? 'Processing...' : '✓ Crop & Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
